/**
 * derivePolicyHealth.js — Pure derivation of policy health from validation output.
 * Extracted from GovernanceSummaryPanel logic. No UI dependencies.
 * Used by TierListPanel for PolicyHealthStrip and per-tier status markers.
 */

import { detectTierOverlaps, ROLE_LABELS } from "./policyBuilderState";

/**
 * Derives a compact policy health structure from validation output.
 *
 * @param {object} validation - { errors, derivedSummary } from validatePolicy()
 * @param {object[]} tiers - Raw tier objects (for overlap detection)
 * @returns {object} { status, issueCount, topIssueSummary, perTierStatusById, perTierIssues }
 */
export function derivePolicyHealth(validation, tiers = []) {
  const { errors = [], derivedSummary } = validation || {};

  if (!derivedSummary?.tiers?.length) {
    return {
      status: "ok",
      issueCount: 0,
      topIssueSummary: "",
      perTierStatusById: {},
      perTierIssues: {},
    };
  }

  const blockingReasons = errors.filter(
    (e) => e.field !== "name" && e.field !== "object_type"
  );
  const allCompliant = derivedSummary.tiers.every((t) => t.isCompliant);
  const hasNoStepsAnywhere =
    tiers.length > 0 &&
    tiers.every((t) => !t.chain || t.chain.length === 0);
  const isFullyCompliant =
    allCompliant && blockingReasons.length === 0 && !hasNoStepsAnywhere;

  const overlaps = tiers.length ? detectTierOverlaps(tiers) : [];
  const overlapTierIds = new Set(overlaps.flat());

  const perTierIssues = {};
  for (const tier of derivedSummary.tiers) {
    const rawTier = tiers.find((t) => t.id === tier.tierId) || {};
    const issues = [];
    if (!tier.isCompliant && tier.missingRoles?.length > 0) {
      const labels = tier.missingRoles.map((r) => ROLE_LABELS[r] || r);
      issues.push({ message: `Missing: ${labels.join(", ")}` });
    }
    for (const e of errors) {
      if (e.tierId === tier.tierId) issues.push(e);
    }
    if (overlapTierIds.has(tier.tierId)) {
      issues.push({ message: "Tier amount range overlaps with another" });
    }
    if (hasNoStepsAnywhere && (!rawTier.chain || rawTier.chain.length === 0)) {
      issues.push({ message: "No approval steps defined" });
    }
    perTierIssues[tier.tierId] = issues;
  }

  const issueCount =
    blockingReasons.length + (hasNoStepsAnywhere ? 1 : 0);
  const firstBlocking = blockingReasons[0];
  const firstNonCompliant = derivedSummary.tiers.find((t) => !t.isCompliant);
  const topIssueSummary =
    firstBlocking?.message ||
    (firstNonCompliant ? "Some tiers have issues" : "") ||
    (hasNoStepsAnywhere ? "No approval steps defined" : "");

  const perTierStatusById = {};
  for (const tier of derivedSummary.tiers) {
    perTierStatusById[tier.tierId] =
      perTierIssues[tier.tierId]?.length > 0 ? "warn" : "ok";
  }

  return {
    status: isFullyCompliant ? "ok" : "warn",
    issueCount,
    topIssueSummary,
    perTierStatusById,
    perTierIssues,
  };
}
